import { useClientTheme } from "@/app/components/ThemeContext"

interface KPICardProps {
  label: string
  value: string | number
  // Kept for API compatibility — the flat Light Forward card has no accent
  // bar, but callers still pass their kpiAccents entry.
  accentColor: string
  /** Override the value colour (e.g. divestiture red for negative totals). */
  valueColor?: string
  trend?: string
  icon?: React.ReactNode
}

// Flat borderless stat card (Light Forward design). On the landtrack light
// theme the page is paper and cards are white; other themes inherit via tokens.
export function KPICard({ label, value, valueColor, trend, icon }: KPICardProps) {
  const { theme } = useClientTheme()

  return (
    <div
      style={{
        backgroundColor: theme.bgSurface,
        borderRadius: "8px",
        padding: "14px 16px",
      }}
    >
      {/* Label row */}
      <div
        className="flex items-center justify-between"
        style={{ gap: "8px", marginBottom: "6px" }}
      >
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: "10px",
            fontWeight: 500,
            color: theme.textMuted,
            letterSpacing: "0.05em",
            textTransform: "uppercase" as const,
          }}
        >
          {label}
        </span>
        {icon && (
          <span
            className="flex items-center justify-center flex-shrink-0 rounded-md"
            style={{
              width: "26px",
              height: "26px",
              backgroundColor: `${theme.brandAccent}14`,
              color: theme.brandAccent,
            }}
          >
            {icon}
          </span>
        )}
      </div>

      {/* Value */}
      <div
        style={{
          fontFamily: "var(--font-data)",
          fontSize: "28px",
          fontWeight: 600,
          color: valueColor ?? theme.textPrimary,
          lineHeight: "1.2",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>

      {/* Trend */}
      {trend && (
        <div className="mt-2">
          <span style={{ fontFamily: "var(--font-ui)", fontSize: "11px", color: theme.textMuted }}>
            {trend}
          </span>
        </div>
      )}
    </div>
  )
}
